import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import {
  Search,
  Download,
  RefreshCw,
  Edit,
  Share2,
  Filter,
  X,
  Info,
  MapPin,
  Briefcase,
  Zap,
  Flame,
  Droplets,
  TrendingUp,
  TrendingDown,
  Calendar as CalendarIcon,
  RotateCcw,
  Save,
  Building,
  Users,
  Home,
  FileText,
  ArrowRight,
  DollarSign,
  Leaf,
  Layers,
  Target,
  AlertCircle,
  CheckCircle,
  Activity
} from 'lucide-react';
import { useTumDashboard, DashboardFilters } from '@/hooks/useTumDashboard';
import { useTumCustomers } from '@/hooks/useTumCustomers';
import { useTumUtilityVendors } from '@/hooks/useTumUtilityVendors';
import { useTumUtilityAccounts } from '@/hooks/useTumUtilityAccounts';
import { useTumProperties } from '@/hooks/useTumProperties';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';

const DATE_RANGES = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 3 months', value: 3 },
  { label: 'Last 6 months', value: 6 },
  { label: 'Last 12 months', value: 12 },
  { label: 'Custom', value: 'custom' },
] as const;

export default function TumDashboardHome() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  
  // Filter state
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [dateRangeType, setDateRangeType] = useState<number | 'custom'>(6);
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({});

  // Fetch filter options
  const { customers } = useTumCustomers();
  const { vendors } = useTumUtilityVendors();
  const { accounts } = useTumUtilityAccounts();
  const { properties } = useTumProperties();

  // Calculate date range - ensure dates are not in the future
  const dateRange = useMemo(() => {
    const now = new Date();
    let from: Date;
    let to: Date;

    if (dateRangeType === 'custom') {
      if (customDateRange.from && customDateRange.to) {
        from = startOfMonth(customDateRange.from);
        to = endOfMonth(customDateRange.to);
      } else {
        // Default to last 6 months if custom not set
        from = startOfMonth(subMonths(now, 6));
        to = endOfMonth(now);
      }
    } else {
      from = startOfMonth(subMonths(now, dateRangeType));
      to = endOfMonth(now);
    }

    // Ensure dates are not in the future
    if (from > now) from = startOfMonth(subMonths(now, 6));
    if (to > now) to = endOfMonth(now);
    if (to < from) to = endOfMonth(now);

    return { from, to };
  }, [dateRangeType, customDateRange]);

  // Build filters
  const filters: DashboardFilters = useMemo(() => ({
    customerIds: selectedCustomers.length > 0 ? selectedCustomers : undefined,
    vendorIds: selectedVendors.length > 0 ? selectedVendors : undefined,
    accountIds: selectedAccounts.length > 0 ? selectedAccounts : undefined,
    siteIds: selectedSites.length > 0 ? selectedSites : undefined,
    dateRange,
  }), [selectedCustomers, selectedVendors, selectedAccounts, selectedSites, dateRange]);

  // Fetch dashboard data
  const { data, isLoading, error } = useTumDashboard(filters);

  // Reset filters
  const resetFilters = () => {
    setSelectedCustomers([]);
    setSelectedVendors([]);
    setSelectedAccounts([]);
    setSelectedSites([]);
    setDateRangeType(6);
    setCustomDateRange({});
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format number with unit
  const formatNumber = (value: number, unit: string) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)} M ${unit}`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)} k ${unit}`;
    }
    return `${value.toFixed(2)} ${unit}`;
  };

  // Generate sample data if no real data available - MUST be before any conditional returns
  const hasData = data && (
    (data.spendComparison && data.spendComparison.length > 0) ||
    (data.kpis && data.kpis.totalSpend > 0)
  );

  // Sample data for demonstration - MUST be before any conditional returns
  const sampleData = useMemo(() => {
    if (hasData && data) return data;

    // Format currency helper for sample data
    const formatCurrencyLocal = (value: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    };

    const sampleSites = (properties.length > 0 ? properties : [
      { id: '1', property_name: 'Action Dallas' },
      { id: '2', property_name: 'Action Houston' },
      { id: '3', property_name: 'Action Kansas City' },
      { id: '4', property_name: 'Action Rock Hill' },
      { id: '5', property_name: 'Berlin' },
      { id: '6', property_name: 'Central Tube Middle Rd' },
      { id: '7', property_name: 'Central Tube Robins St' },
      { id: '8', property_name: 'Olympic Gary' },
      { id: '9', property_name: 'Olympic KY Owens' },
      { id: '10', property_name: 'Integrity Latrobe' },
    ]).slice(0, 10).map((p: any, i: number) => {
      const currentSpend = Math.random() * 50000 + 10000;
      const previousSpend = Math.random() * 45000 + 9000;
      return {
        siteId: p.id || `site-${i}`,
        siteName: p.property_name || `Site ${i + 1}`,
        utilities: ['electricity', 'gas', 'water'].slice(0, Math.floor(Math.random() * 3) + 1),
        currentPeriodSpend: currentSpend,
        previousPeriodSpend: previousSpend,
        variance: currentSpend - previousSpend,
        variancePercent: previousSpend > 0 ? ((currentSpend - previousSpend) / previousSpend) * 100 : 0,
      };
    });

    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sampleMonthlySpend = months.map(month => {
      const elec = Math.random() * 15000;
      const gas = Math.random() * 12000;
      const water = Math.random() * 8000;
      return {
        month,
        electricity: elec,
        gas: gas,
        water: water,
        total: elec + gas + water,
      };
    });

    const siteCount = properties.length || 44;
    const gasSites = Math.floor(siteCount * 0.73);
    const waterSites = Math.floor(siteCount * 0.73);

    return {
      kpis: {
        totalSpend: 100000,
        totalEmissions: 982.74,
        totalSites: siteCount,
      },
      utilityMetrics: {
        electricity: {
          totalKwh: 0,
          totalSpend: 0,
          sitesCount: Math.floor(siteCount * 0.86) || 38,
          accountsCount: 149,
        },
        gas: {
          totalTherms: 18500,
          totalSpend: 37800,
          sitesCount: gasSites || 32,
          accountsCount: 122,
        },
        water: {
          totalGallons: 53940000,
          totalSpend: 62200,
          sitesCount: waterSites || 32,
          accountsCount: 124,
        },
      },
      spendComparison: sampleSites,
      monthlySpend: sampleMonthlySpend,
      electricityPerformance: months.map((month, i) => ({
        month: `${String(i + 7).padStart(2, '0')}/01`,
        usage: 0,
        spend: 0,
      })),
      gasPerformance: months.map((month, i) => ({
        month: `${String(i + 7).padStart(2, '0')}/01`,
        usage: Math.random() * 8000 + 2000,
        spend: Math.random() * 10000 + 3000,
        emissions: Math.random() * 300 + 50,
      })),
      waterPerformance: months.map((month, i) => ({
        month: `${String(i + 7).padStart(2, '0')}/01`,
        usage: Math.random() * 16000 + 4000,
        spend: Math.random() * 12000 + 2000,
      })),
      electricityUsageComparison: [],
      gasUsageComparison: sampleSites.slice(0, 7).map((site, i) => {
        const usage = Math.random() * 2 + 0.4;
        const spend = Math.random() * 5000 + 200;
        return {
          siteId: site.siteId,
          siteName: site.siteName,
          usage: usage,
          spend: spend,
          unitCost: usage > 0 ? spend / usage : 0,
        };
      }),
      waterUsageComparison: sampleSites.slice(0, 8).map((site, i) => {
        const usage = Math.random() * 200 + 2;
        const spend = Math.random() * 2000 + 100;
        return {
          siteId: site.siteId,
          siteName: site.siteName,
          usage: usage,
          spend: spend,
          unitCost: usage > 0 ? spend / usage : 0,
        };
      }),
      locationData: sampleSites.map((site, i) => {
        // Generate sample coordinates for demo
        const hash = site.siteId.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
        const cities = ['Dallas', 'Houston', 'Kansas City', 'Rock Hill', 'Berlin', 'Gary', 'Owens', 'Latrobe'];
        const states = ['TX', 'TX', 'MO', 'SC', 'CT', 'IN', 'KY', 'PA'];
        return {
          siteId: site.siteId,
          siteName: site.siteName,
          latitude: 39.8283 + (hash % 100) / 1000,
          longitude: -98.5795 + (hash % 100) / 1000,
          address: `Sample Address ${i + 1}`,
          city: cities[i % cities.length] || 'Unknown',
          state: states[i % states.length] || 'XX',
          zipCode: `${10000 + i}`,
          totalSpend: site.currentPeriodSpend,
          utilityTypes: site.utilities,
        };
      }),
      locationInsights: [
        { region: 'TX', siteCount: 3, totalSpend: 45000, avgSpendPerSite: 15000, topUtility: 'water' },
        { region: 'MO', siteCount: 2, totalSpend: 28000, avgSpendPerSite: 14000, topUtility: 'gas' },
        { region: 'SC', siteCount: 1, totalSpend: 12000, avgSpendPerSite: 12000, topUtility: 'electricity' },
        { region: 'CT', siteCount: 1, totalSpend: 15000, avgSpendPerSite: 15000, topUtility: 'water' },
      ],
      smartMetrics: [
        {
          id: 'avg-spend',
          title: 'Avg Spend per Site',
          value: formatCurrencyLocal(10000),
          change: 5.2,
          changeType: 'increase' as const,
          description: 'Average utility spend per location',
          icon: 'dollar-sign',
          trend: 'up' as const,
        },
        {
          id: 'top-site',
          title: 'Top Spending Site',
          value: sampleSites[0]?.siteName || 'N/A',
          description: `Spending ${formatCurrencyLocal(sampleSites[0]?.currentPeriodSpend || 0)}`,
          icon: 'building',
        },
        {
          id: 'efficiency',
          title: 'Energy Efficiency',
          value: '12.5 kWh/$',
          description: 'Energy output per dollar spent',
          icon: 'zap',
          trend: 'up' as const,
        },
        {
          id: 'coverage',
          title: 'Geographic Coverage',
          value: 5,
          description: '5 regions with active sites',
          icon: 'map-pin',
        },
        {
          id: 'util-diversity',
          title: 'Utility Diversity',
          value: 3,
          description: 'Number of utility types in use',
          icon: 'layers',
        },
        {
          id: 'emissions-intensity',
          title: 'Emissions Intensity',
          value: '9.83 t/$1K',
          description: 'CO₂e emissions per $1,000 spent',
          icon: 'leaf',
          trend: 'down' as const,
        },
      ],
    };
  }, [hasData, data, properties]);

  const displayData = hasData && data ? data : sampleData;

  // Handle drill-down
  const handleSiteClick = (siteId: string) => {
    navigate(`/w/${currentWorkspace?.id}/tum/properties`);
    // Could navigate to specific site detail page if exists
  };

  const handleAccountClick = (accountId: string) => {
    navigate(`/w/${currentWorkspace?.id}/tum/bills`);
  };

  // Show error state but still render the dashboard with sample data
  if (error) {
    console.error('Dashboard data error:', error);
  }

  return (
    <ModuleAccessGuard moduleId="tum">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      <div className="space-y-6 p-6 bg-gradient-to-br from-gray-50 via-white to-gray-50 min-h-screen">
        {/* Error Banner */}
        {error && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-yellow-800">
                <Info className="h-4 w-4" />
                <span className="text-sm">
                  Unable to load live data. Showing sample data for demonstration.
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">TUM Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Utility Management Overview
            </p>
          </div>
          <div className="flex items-center gap-2">
            <WorkspaceSwitcher />
            <Select value={dateRangeType.toString()} onValueChange={(v) => setDateRangeType(v === 'custom' ? 'custom' : Number(v))}>
              <SelectTrigger className="w-[180px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value.toString()}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dateRangeType === 'custom' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    {customDateRange.from && customDateRange.to
                      ? `${format(customDateRange.from, 'MMM dd')} - ${format(customDateRange.to, 'MMM dd')}`
                      : 'Select dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={{ from: customDateRange.from, to: customDateRange.to }}
                    onSelect={(range) => setCustomDateRange({ from: range?.from, to: range?.to })}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}
            <Button variant="outline" size="sm">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/w/${currentWorkspace?.id}/tum/properties`)}>
            <CardHeader className="bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <Home className="h-5 w-5" />
                    Properties
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Manage properties and sites
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-gray-600">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/w/${currentWorkspace?.id}/tum/bills`)}>
            <CardHeader className="bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <FileText className="h-5 w-5" />
                    Utility Bills
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    View and manage bills
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-gray-600">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/w/${currentWorkspace?.id}/tum/properties/tenants`)}>
            <CardHeader className="bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <Users className="h-5 w-5" />
                    Tenants
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Manage tenant accounts
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-gray-600">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/w/${currentWorkspace?.id}/tum/reports`)}>
            <CardHeader className="bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <Building className="h-5 w-5" />
                    Reports
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Analytics and reports
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-gray-600">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Global Filter Bar - Sticky */}
        <Card className="sticky top-0 z-10 border-b bg-white shadow-sm">
          <CardContent className="p-4 bg-white">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              {/* Customer Filter */}
              {customers.length > 1 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[180px] justify-between">
                      <span>Customer: {selectedCustomers.length > 0 ? `${selectedCustomers.length} selected` : 'All'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[250px] p-0">
                    <div className="p-2 border-b">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Select Customers</span>
                        {selectedCustomers.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedCustomers([])}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto p-2">
                      {customers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                          onClick={() => {
                            if (selectedCustomers.includes(customer.id)) {
                              setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id));
                            } else {
                              setSelectedCustomers([...selectedCustomers, customer.id]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCustomers.includes(customer.id)}
                            onChange={() => {}}
                            className="rounded"
                          />
                          <span className="text-sm">{customer.customer_name}</span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Vendor Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between">
                    <span>Vendor: {selectedVendors.length > 0 ? `${selectedVendors.length} selected` : 'All'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0">
                  <div className="p-2 border-b">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Select Vendors</span>
                      {selectedVendors.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedVendors([])}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-2">
                    {vendors.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                        onClick={() => {
                          if (selectedVendors.includes(vendor.id)) {
                            setSelectedVendors(selectedVendors.filter(id => id !== vendor.id));
                          } else {
                            setSelectedVendors([...selectedVendors, vendor.id]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedVendors.includes(vendor.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <span className="text-sm">{vendor.vendor_name}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Account Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between">
                    <span>Account: {selectedAccounts.length > 0 ? `${selectedAccounts.length} selected` : 'All'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <div className="p-2 border-b">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Select Accounts</span>
                      {selectedAccounts.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedAccounts([])}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Search accounts..."
                      className="mt-2"
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-2">
                    {accounts.slice(0, 100).map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                        onClick={() => {
                          if (selectedAccounts.includes(account.id)) {
                            setSelectedAccounts(selectedAccounts.filter(id => id !== account.id));
                          } else {
                            setSelectedAccounts([...selectedAccounts, account.id]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <span className="text-sm">{account.account_number}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Site Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between">
                    <span>Site: {selectedSites.length > 0 ? `${selectedSites.length} selected` : 'All'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <div className="p-2 border-b">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Select Sites</span>
                      {selectedSites.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedSites([])}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Search sites..."
                      className="mt-2"
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-2">
                    {properties.slice(0, 100).map((property) => (
                      <div
                        key={property.id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-accent cursor-pointer"
                        onClick={() => {
                          if (selectedSites.includes(property.id)) {
                            setSelectedSites(selectedSites.filter(id => id !== property.id));
                          } else {
                            setSelectedSites([...selectedSites, property.id]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSites.includes(property.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <span className="text-sm">{property.property_name}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Reset Filters */}
              {(selectedCustomers.length > 0 || selectedVendors.length > 0 || 
                selectedAccounts.length > 0 || selectedSites.length > 0) && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              )}

              {/* Save View (optional) */}
              <Button variant="ghost" size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save View
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Summary Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Spend Comparison Table */}
              <Card className="bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-gray-900 font-semibold">Spend Comparison</CardTitle>
                      <Info className="h-4 w-4 text-blue-500 cursor-help" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="hover:bg-white/80">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="hover:bg-white/80">CSV</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10">
                        <TableRow className="border-b border-gray-200">
                          <TableHead className="font-semibold text-gray-700">Site</TableHead>
                          <TableHead className="font-semibold text-gray-700">Utilities</TableHead>
                          <TableHead className="text-right font-semibold text-gray-700">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayData?.spendComparison && displayData.spendComparison.length > 0 ? (
                          displayData.spendComparison.slice(0, 10).map((item, index) => (
                            <TableRow
                              key={item.siteId}
                              className="cursor-pointer hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-200 border-b border-gray-100 group"
                              onClick={() => handleSiteClick(item.siteId)}
                            >
                              <TableCell className="font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
                                {item.siteName}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1.5 flex-wrap">
                                  {item.utilities.map((util) => (
                                    <Badge 
                                      key={util} 
                                      className={`text-xs font-medium px-2 py-0.5 ${
                                        util === 'electricity' ? 'bg-red-100 text-red-700 border-red-200' :
                                        util === 'gas' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                        util === 'water' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                        'bg-gray-100 text-gray-700 border-gray-200'
                                      }`}
                                    >
                                      {util.charAt(0).toUpperCase() + util.slice(1)}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                                {formatCurrency(item.currentPeriodSpend)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                              No data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Map View with Location Data */}
              <Card className="bg-gradient-to-br from-white to-blue-50/30 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50/50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-gray-900 font-semibold">Geographic Distribution</CardTitle>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                      {displayData?.locationData?.length || 0} Locations
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[400px] bg-gradient-to-br from-blue-50 via-indigo-50/30 to-gray-50 rounded-lg relative overflow-hidden border border-gray-200">
                    {/* Map background with grid */}
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDIwIDAgTCAwIDAgMCAyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZGRkIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSJ3aGl0ZSIvPjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
                    
                    {/* Location markers with real coordinates */}
                    {displayData?.locationData && displayData.locationData.length > 0 ? (
                      displayData.locationData.map((location, index) => {
                        // Normalize coordinates to map viewport (assuming US coordinates)
                        // This is a simplified mapping - in production, use a proper map library
                        const normalizedLat = location.latitude || 39.8283;
                        const normalizedLng = location.longitude || -98.5795;
                        
                        // Convert to percentage for display (simplified projection)
                        const latRange = 25; // Approximate US latitude range
                        const lngRange = 50; // Approximate US longitude range
                        const baseLat = 25; // Southern US
                        const baseLng = -125; // Western US
                        
                        const x = ((normalizedLng - baseLng) / lngRange) * 100;
                        const y = 100 - ((normalizedLat - baseLat) / latRange) * 100;
                        
                        // Determine marker color based on spend
                        const spendIntensity = location.totalSpend;
                        const maxSpend = Math.max(...displayData.locationData.map(l => l.totalSpend));
                        const intensity = maxSpend > 0 ? spendIntensity / maxSpend : 0;
                        
                        const markerColor = intensity > 0.7 ? '#ef4444' : intensity > 0.4 ? '#f97316' : '#3b82f6';
                        const markerColorDark = intensity > 0.7 ? '#dc2626' : intensity > 0.4 ? '#ea580c' : '#2563eb';
                        
                        return (
                          <div
                            key={location.siteId}
                            className="absolute group cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
                            style={{ 
                              left: `${Math.max(5, Math.min(95, x))}%`, 
                              top: `${Math.max(5, Math.min(95, y))}%`,
                              zIndex: Math.floor(intensity * 10)
                            }}
                            onClick={() => handleSiteClick(location.siteId)}
                          >
                            <div className="relative">
                              <MapPin 
                                className="h-6 w-6 drop-shadow-lg transition-all group-hover:scale-150 group-hover:drop-shadow-xl" 
                                style={{ color: markerColor }}
                              />
                              <div 
                                className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white animate-pulse" 
                                style={{ backgroundColor: markerColorDark }}
                              />
                              <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-xl border border-gray-200 opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap text-xs font-medium text-gray-700 z-20 min-w-[150px]">
                                <div className="font-semibold text-gray-900 mb-1">{location.siteName}</div>
                                <div className="text-gray-600">{location.city && location.state ? `${location.city}, ${location.state}` : location.address}</div>
                                <div className="text-blue-600 font-semibold mt-1">{formatCurrency(location.totalSpend)}</div>
                                <div className="flex gap-1 mt-1">
                                  {location.utilityTypes.map(util => (
                                    <Badge key={util} variant="outline" className="text-[10px] px-1 py-0">
                                      {util.charAt(0).toUpperCase()}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      // Fallback to property-based markers if no location data
                      properties.slice(0, 15).map((property, index) => {
                        const x = 10 + (index * 7) % 80;
                        const y = 15 + Math.floor((index * 7) / 80) * 25;
                        return (
                          <div
                            key={property.id}
                            className="absolute group cursor-pointer"
                            style={{ left: `${x}%`, top: `${y}%` }}
                            onClick={() => handleSiteClick(property.id)}
                          >
                            <div className="relative">
                              <MapPin className="h-6 w-6 text-red-500 drop-shadow-lg transition-transform group-hover:scale-125" />
                              <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-600 rounded-full border-2 border-white animate-pulse" />
                              <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-xs font-medium text-gray-700 z-10">
                                {property.property_name}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Legend with spend intensity */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm p-4 rounded-lg shadow-xl border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-semibold text-gray-900">Site Locations</span>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-red-500"></div>
                          <span className="text-gray-600">High Spend (&gt;70%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                          <span className="text-gray-600">Medium Spend (40-70%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                          <span className="text-gray-600">Low Spend (&lt;40%)</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
                        {displayData?.locationData?.length || properties.length} locations mapped
                      </div>
                    </div>

                    {/* Location insights summary */}
                    {displayData?.locationInsights && displayData.locationInsights.length > 0 && (
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-xl border border-gray-200 max-w-[200px]">
                        <div className="text-xs font-semibold text-gray-900 mb-2">Top Regions</div>
                        <div className="space-y-1">
                          {displayData.locationInsights.slice(0, 3).map((insight, idx) => (
                            <div key={idx} className="text-xs text-gray-600">
                              <span className="font-medium">{insight.region}:</span> {insight.siteCount} sites
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Spend Chart */}
              <Card className="bg-gradient-to-br from-white to-blue-50/30 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50/50 border-b border-gray-200">
                  <CardTitle className="text-gray-900 font-semibold">Monthly Spend</CardTitle>
                </CardHeader>
                <CardContent className="bg-white p-6">
                  <div className="h-[400px]">
                    {displayData?.monthlySpend && displayData.monthlySpend.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={displayData.monthlySpend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="electricityGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#dc2626" stopOpacity={0.7} />
                            </linearGradient>
                            <linearGradient id="waterGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#2563eb" stopOpacity={0.7} />
                            </linearGradient>
                            <linearGradient id="gasGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f97316" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#ea580c" stopOpacity={0.7} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                          <XAxis 
                            dataKey="month" 
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={{ stroke: '#d1d5db' }}
                          />
                          <YAxis 
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value: number) => formatCurrency(value)} 
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            iconType="rect"
                          />
                          <Bar 
                            dataKey="electricity" 
                            stackId="a" 
                            fill="url(#electricityGradient)" 
                            name="Electricity"
                            radius={[0, 0, 0, 0]}
                          />
                          <Bar 
                            dataKey="water" 
                            stackId="a" 
                            fill="url(#waterGradient)" 
                            name="Water"
                            radius={[0, 0, 0, 0]}
                          />
                          <Bar 
                            dataKey="gas" 
                            stackId="a" 
                            fill="url(#gasGradient)" 
                            name="Gas"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-blue-100/50 to-indigo-100/50 border-b border-blue-200">
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <Briefcase className="h-5 w-5" />
                    Total Spend
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-white/50 p-6">
                  <div className="text-4xl font-bold text-blue-900 mb-2">
                    {displayData?.kpis ? formatCurrency(displayData.kpis.totalSpend) : '$0'}
                  </div>
                  <p className="text-sm text-blue-700/70">Across all utilities</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-green-100/50 to-emerald-100/50 border-b border-green-200">
                  <CardTitle className="text-green-900 font-semibold">Total Emissions CO₂e</CardTitle>
                </CardHeader>
                <CardContent className="bg-white/50 p-6">
                  <div className="text-4xl font-bold text-green-900 mb-2">
                    {displayData?.kpis ? `${displayData.kpis.totalEmissions.toFixed(2)} t` : '0.00 t'}
                  </div>
                  <p className="text-sm text-green-700/70">Carbon footprint</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-purple-100/50 to-pink-100/50 border-b border-purple-200">
                  <CardTitle className="text-purple-900 font-semibold">Sites</CardTitle>
                </CardHeader>
                <CardContent className="bg-white/50 p-6">
                  <div className="text-4xl font-bold text-purple-900 mb-2">
                    {displayData?.kpis ? displayData.kpis.totalSites : 0}
                  </div>
                  <p className="text-sm text-purple-700/70">Active locations</p>
                </CardContent>
              </Card>
            </div>

            {/* Utility Consumption Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Electricity */}
              <Card className="border-red-200 bg-gradient-to-br from-red-50 via-red-50/50 to-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-red-100 to-red-50 border-b border-red-200">
                  <CardTitle className="flex items-center gap-2 text-red-800 font-semibold">
                    <div className="p-2 bg-red-200/50 rounded-lg">
                      <Zap className="h-5 w-5 text-red-700" />
                    </div>
                    Electricity
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-white/80 backdrop-blur-sm p-6">
                  {displayData?.utilityMetrics.electricity.totalKwh > 0 ? (
                    <>
                      <div className="text-3xl font-bold mb-2 text-red-900 bg-gradient-to-r from-red-900 to-red-700 bg-clip-text text-transparent">
                        {formatNumber(displayData.utilityMetrics.electricity.totalKwh, 'kWh')}
                      </div>
                      <div className="h-1 w-full bg-red-100 rounded-full mb-4">
                        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-red-50/50 p-3 rounded-lg border border-red-100">
                          <div className="text-xs text-red-700/70 font-medium uppercase tracking-wide">Sites</div>
                          <div className="text-xl font-bold text-red-900 mt-1">
                            {displayData.utilityMetrics.electricity.sitesCount}
                          </div>
                        </div>
                        <div className="bg-red-50/50 p-3 rounded-lg border border-red-100">
                          <div className="text-xs text-red-700/70 font-medium uppercase tracking-wide">Accounts</div>
                          <div className="text-xl font-bold text-red-900 mt-1">
                            {displayData.utilityMetrics.electricity.accountsCount}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No Electricity Data
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Gas */}
              <Card className="border-orange-200 bg-gradient-to-br from-orange-50 via-orange-50/50 to-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-orange-100 to-orange-50 border-b border-orange-200">
                  <CardTitle className="flex items-center gap-2 text-orange-800 font-semibold">
                    <div className="p-2 bg-orange-200/50 rounded-lg">
                      <Flame className="h-5 w-5 text-orange-700" />
                    </div>
                    Gas
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-white/80 backdrop-blur-sm p-6">
                  {displayData?.utilityMetrics.gas.totalTherms > 0 ? (
                    <>
                      <div className="text-3xl font-bold mb-2 text-orange-900 bg-gradient-to-r from-orange-900 to-orange-700 bg-clip-text text-transparent">
                        {formatNumber(displayData.utilityMetrics.gas.totalTherms, 'therms')}
                      </div>
                      <div className="h-1 w-full bg-orange-100 rounded-full mb-4">
                        <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full" style={{ width: '65%' }}></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-orange-50/50 p-3 rounded-lg border border-orange-100">
                          <div className="text-xs text-orange-700/70 font-medium uppercase tracking-wide">Sites</div>
                          <div className="text-xl font-bold text-orange-900 mt-1">
                            {displayData.utilityMetrics.gas.sitesCount}
                          </div>
                        </div>
                        <div className="bg-orange-50/50 p-3 rounded-lg border border-orange-100">
                          <div className="text-xs text-orange-700/70 font-medium uppercase tracking-wide">Accounts</div>
                          <div className="text-xl font-bold text-orange-900 mt-1">
                            {displayData.utilityMetrics.gas.accountsCount}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No Gas Data
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Water */}
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 via-blue-50/50 to-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="bg-gradient-to-r from-blue-100 to-blue-50 border-b border-blue-200">
                  <CardTitle className="flex items-center gap-2 text-blue-800 font-semibold">
                    <div className="p-2 bg-blue-200/50 rounded-lg">
                      <Droplets className="h-5 w-5 text-blue-700" />
                    </div>
                    Water
                  </CardTitle>
                </CardHeader>
                <CardContent className="bg-white/80 backdrop-blur-sm p-6">
                  {displayData?.utilityMetrics.water.totalGallons > 0 ? (
                    <>
                      <div className="text-3xl font-bold mb-2 text-blue-900 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
                        {formatNumber(displayData.utilityMetrics.water.totalGallons, 'gal')}
                      </div>
                      <div className="h-1 w-full bg-blue-100 rounded-full mb-4">
                        <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                          <div className="text-xs text-blue-700/70 font-medium uppercase tracking-wide">Sites</div>
                          <div className="text-xl font-bold text-blue-900 mt-1">
                            {displayData.utilityMetrics.water.sitesCount}
                          </div>
                        </div>
                        <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                          <div className="text-xs text-blue-700/70 font-medium uppercase tracking-wide">Accounts</div>
                          <div className="text-xl font-bold text-blue-900 mt-1">
                            {displayData.utilityMetrics.water.accountsCount}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No Water Data
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Smart Metrics & Insights Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Smart Metrics & Insights
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayData?.smartMetrics && displayData.smartMetrics.map((metric) => {
                  const IconComponent = 
                    metric.icon === 'dollar-sign' ? DollarSign :
                    metric.icon === 'building' ? Building :
                    metric.icon === 'zap' ? Zap :
                    metric.icon === 'map-pin' ? MapPin :
                    metric.icon === 'layers' ? Layers :
                    metric.icon === 'leaf' ? Leaf :
                    Activity;

                  return (
                    <Card 
                      key={metric.id} 
                      className="bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <div className={`p-2 rounded-lg ${
                              metric.trend === 'up' ? 'bg-green-100' :
                              metric.trend === 'down' ? 'bg-red-100' :
                              'bg-blue-100'
                            }`}>
                              <IconComponent className={`h-4 w-4 ${
                                metric.trend === 'up' ? 'text-green-700' :
                                metric.trend === 'down' ? 'text-red-700' :
                                'text-blue-700'
                              }`} />
                            </div>
                            {metric.title}
                          </CardTitle>
                          {metric.change !== undefined && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                metric.changeType === 'increase' ? 'bg-red-50 text-red-700 border-red-200' :
                                metric.changeType === 'decrease' ? 'bg-green-50 text-green-700 border-green-200' :
                                'bg-gray-50 text-gray-700 border-gray-200'
                              }`}
                            >
                              {metric.changeType === 'increase' ? <TrendingUp className="h-3 w-3 mr-1" /> :
                               metric.changeType === 'decrease' ? <TrendingDown className="h-3 w-3 mr-1" /> : null}
                              {metric.change !== undefined && `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%`}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
                        </div>
                        <p className="text-xs text-gray-600">{metric.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Location Insights Section */}
            {displayData?.locationInsights && displayData.locationInsights.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-6 text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Regional Insights
                </h2>
                <Card className="bg-gradient-to-br from-white to-indigo-50/30 border border-gray-200 shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50/50 border-b border-gray-200">
                    <CardTitle className="text-gray-900 font-semibold">Geographic Performance by Region</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-indigo-50/95 backdrop-blur-sm z-10">
                          <TableRow className="border-b border-indigo-100">
                            <TableHead className="font-semibold text-gray-700">Region</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-right">Sites</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-right">Total Spend</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-right">Avg per Site</TableHead>
                            <TableHead className="font-semibold text-gray-700">Top Utility</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {displayData.locationInsights
                            .sort((a, b) => b.totalSpend - a.totalSpend)
                            .map((insight, index) => (
                            <TableRow 
                              key={insight.region}
                              className="hover:bg-indigo-50/50 transition-colors border-b border-gray-100"
                            >
                              <TableCell className="font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <div className={`h-2 w-2 rounded-full ${
                                    index === 0 ? 'bg-yellow-400' :
                                    index === 1 ? 'bg-gray-400' :
                                    index === 2 ? 'bg-orange-400' :
                                    'bg-blue-400'
                                  }`} />
                                  {insight.region}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-gray-900">
                                {insight.siteCount}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-gray-900">
                                {formatCurrency(insight.totalSpend)}
                              </TableCell>
                              <TableCell className="text-right text-gray-700">
                                {formatCurrency(insight.avgSpendPerSite)}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant="outline"
                                  className={
                                    insight.topUtility === 'electricity' ? 'bg-red-100 text-red-700 border-red-200' :
                                    insight.topUtility === 'gas' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                    insight.topUtility === 'water' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                    'bg-gray-100 text-gray-700 border-gray-200'
                                  }
                                >
                                  {insight.topUtility.charAt(0).toUpperCase() + insight.topUtility.slice(1)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Utility Performance Section */}
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Utility Performance
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Electricity Performance */}
                <Card className="bg-white border border-gray-200 shadow-sm">
                  <CardHeader className="bg-gray-50 border-b">
                    <CardTitle className="text-gray-900">Electricity</CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    {displayData?.electricityPerformance && displayData.electricityPerformance.length > 0 ? (
                      <>
                        <div className="h-[250px] mb-4 bg-gradient-to-br from-red-50/30 to-white rounded-lg p-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={displayData.electricityPerformance} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                              <defs>
                                <linearGradient id="electricityBarGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                                  <stop offset="100%" stopColor="#dc2626" stopOpacity={0.6} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#fecaca" opacity={0.3} />
                              <XAxis 
                                dataKey="month" 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#fca5a5' }}
                              />
                              <YAxis 
                                yAxisId="left" 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#fca5a5' }}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                                  border: '1px solid #fecaca',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                                iconType="rect"
                              />
                              <Bar 
                                yAxisId="left" 
                                dataKey="usage" 
                                fill="url(#electricityBarGradient)" 
                                name="Electricity (kWh)"
                                radius={[4, 4, 0, 0]}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto custom-scrollbar rounded-lg border border-gray-200">
                          <Table>
                            <TableHeader className="sticky top-0 bg-red-50/95 backdrop-blur-sm z-10">
                              <TableRow className="border-b border-red-100">
                                <TableHead className="font-semibold text-gray-700">Name</TableHead>
                                <TableHead className="font-semibold text-gray-700">kWh</TableHead>
                                <TableHead className="text-right font-semibold text-gray-700">Spend</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {displayData.electricityUsageComparison.slice(0, 10).map((item) => (
                                <TableRow
                                  key={item.siteId}
                                  className="cursor-pointer hover:bg-red-50/50 transition-colors border-b border-gray-100 group"
                                  onClick={() => handleSiteClick(item.siteId)}
                                >
                                  <TableCell className="font-medium text-gray-900 group-hover:text-red-700">{item.siteName}</TableCell>
                                  <TableCell className="text-gray-700">{formatNumber(item.usage, 'kWh')}</TableCell>
                                  <TableCell className="text-right font-semibold text-gray-900 group-hover:text-red-700">
                                    {formatCurrency(item.spend)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No Electricity Data
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Gas Performance */}
                <Card className="bg-white border border-gray-200 shadow-sm">
                  <CardHeader className="bg-gray-50 border-b">
                    <CardTitle className="text-gray-900">Gas</CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    {displayData?.gasPerformance && displayData.gasPerformance.length > 0 ? (
                      <>
                        <div className="h-[250px] mb-4 bg-gradient-to-br from-orange-50/30 to-white rounded-lg p-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={displayData.gasPerformance} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                              <defs>
                                <linearGradient id="gasBarGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.9} />
                                  <stop offset="100%" stopColor="#ea580c" stopOpacity={0.6} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" opacity={0.3} />
                              <XAxis 
                                dataKey="month" 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#fdba74' }}
                              />
                              <YAxis 
                                yAxisId="left" 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#fdba74' }}
                              />
                              <YAxis 
                                yAxisId="right" 
                                orientation="right"
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#86efac' }}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                                  border: '1px solid #fed7aa',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                                iconType="rect"
                              />
                              <Bar 
                                yAxisId="left" 
                                dataKey="usage" 
                                fill="url(#gasBarGradient)" 
                                name="Gas (therms)"
                                radius={[4, 4, 0, 0]}
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="emissions"
                                stroke="#22c55e"
                                strokeWidth={3}
                                dot={{ fill: '#22c55e', r: 4 }}
                                activeDot={{ r: 6 }}
                                name="Emissions (t)"
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto custom-scrollbar rounded-lg border border-gray-200">
                          <Table>
                            <TableHeader className="sticky top-0 bg-orange-50/95 backdrop-blur-sm z-10">
                              <TableRow className="border-b border-orange-100">
                                <TableHead className="font-semibold text-gray-700">Name</TableHead>
                                <TableHead className="font-semibold text-gray-700">Therms</TableHead>
                                <TableHead className="text-right font-semibold text-gray-700">Spend</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {displayData.gasUsageComparison.slice(0, 10).map((item) => (
                                <TableRow
                                  key={item.siteId}
                                  className="cursor-pointer hover:bg-orange-50/50 transition-colors border-b border-gray-100 group"
                                  onClick={() => handleSiteClick(item.siteId)}
                                >
                                  <TableCell className="font-medium text-gray-900 group-hover:text-orange-700">{item.siteName}</TableCell>
                                  <TableCell className="text-gray-700">{formatNumber(item.usage, 'therms')}</TableCell>
                                  <TableCell className="text-right font-semibold text-gray-900 group-hover:text-orange-700">
                                    {formatCurrency(item.spend)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No Gas Data
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Water Performance */}
                <Card className="bg-white border border-gray-200 shadow-sm">
                  <CardHeader className="bg-gray-50 border-b">
                    <CardTitle className="text-gray-900">Water</CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    {displayData?.waterPerformance && displayData.waterPerformance.length > 0 ? (
                      <>
                        <div className="h-[250px] mb-4 bg-gradient-to-br from-blue-50/30 to-white rounded-lg p-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={displayData.waterPerformance} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                              <defs>
                                <linearGradient id="waterBarGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.6} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#bfdbfe" opacity={0.3} />
                              <XAxis 
                                dataKey="month" 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#93c5fd' }}
                              />
                              <YAxis 
                                tick={{ fill: '#6b7280', fontSize: 11 }}
                                axisLine={{ stroke: '#93c5fd' }}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                                  border: '1px solid #bfdbfe',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                                iconType="rect"
                              />
                              <Bar 
                                dataKey="usage" 
                                fill="url(#waterBarGradient)" 
                                name="Water (gal)"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto custom-scrollbar rounded-lg border border-gray-200">
                          <Table>
                            <TableHeader className="sticky top-0 bg-blue-50/95 backdrop-blur-sm z-10">
                              <TableRow className="border-b border-blue-100">
                                <TableHead className="font-semibold text-gray-700">Name</TableHead>
                                <TableHead className="font-semibold text-gray-700">Gallons</TableHead>
                                <TableHead className="text-right font-semibold text-gray-700">Spend</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {displayData.waterUsageComparison.slice(0, 10).map((item) => (
                                <TableRow
                                  key={item.siteId}
                                  className="cursor-pointer hover:bg-blue-50/50 transition-colors border-b border-gray-100 group"
                                  onClick={() => handleSiteClick(item.siteId)}
                                >
                                  <TableCell className="font-medium text-gray-900 group-hover:text-blue-700">{item.siteName}</TableCell>
                                  <TableCell className="text-gray-700">{formatNumber(item.usage, 'gal')}</TableCell>
                                  <TableCell className="text-right font-semibold text-gray-900 group-hover:text-blue-700">
                                    {formatCurrency(item.spend)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No Water Data
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </ModuleAccessGuard>
  );
}

